import { faPlus, faUpload, faSpinner, faSearch, faArrowRotateLeft, faWarning, faExclamationTriangle, faDownload, faChevronDown, faCancel, faCircleCheck, faCircleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTheme } from '../../../context/ThemeProvider'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { Menu } from '@headlessui/react'

import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import useAuth from '../../../hooks/useAuth'

import CreateStaff from './Components/CreateStaff'
import UploadStaff from './Components/UploadStaff'
import Slideout from '../../../components/SlideOut'

import SideBar from '../../SideBar'

import moment from 'moment'
import Modal from '../../../components/Modal'

const StaffDetails = () => {
  const axios = useAxiosPrivate()
  const { createInfo } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()

  const [archivedGroupedStaff, setArchivedGroupedStaff] = useState({})
  const [groupedStaff, setGroupedStaff] = useState({})
  const [policyData, setPolicyData] = useState({})
  const [orgType, setOrgType] = useState(null)

  const [activeView, setActiveView] = useState('form')
  const [activeTab, setActiveTab] = useState('live')
  const [searchTerm, setSearchTerm] = useState('')
  const [bulkMode, setBulkMode] = useState(false)

  const [isArchivedContext, setIsArchivedContext] = useState(false)
  const [selectedStaffIds, setSelectedStaffIds] = useState([])
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [mode, setMode] = useState('create')

  const [uploadMessage, setUploadMessage] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadWarnings, setUploadWarnings] = useState([])
  const [uploadRows, setUploadRows] = useState([])
  
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [pendingHref, setPendingHref] = useState(null)
  const [canSubmit, setCanSubmit] = useState(false)
  const [activeSlideOut, setActiveSlideOut] = useState(null)
  const [slideOutTitle, setSlideOutTitle] = useState('')

  const [isRevertingAll, setIsRevertingAll] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloadingStaffList, setIsDownloadingStaffList] = useState(false)
  const [groupPages, setGroupPages] = useState({})
  const submitPressed = useRef(false)

  const pageSize = 10

  const setCreateSlideOutOpen = (isOpen) => {
    setActiveSlideOut(isOpen ? 'create' : null)
  }

  const setUploadSlideOutOpen = (isOpen) => {
    setActiveSlideOut(isOpen ? 'upload' : null)
  }

  const setSlideOutOpen = (isOpen) => {
    if (!isOpen) {
      setActiveSlideOut(null)
      setSlideOutTitle('')
    }
  }

  useEffect(() => {
      if (!showUploadModal) return;

      const html = document.documentElement;
      const body = document.body;

      const previousHtmlOverflow = html.style.overflow;
      const previousBodyOverflow = body.style.overflow;
      const previousBodyPosition = body.style.position;
      const previousBodyTop = body.style.top;
      const previousBodyWidth = body.style.width;

      const scrollY = window.scrollY;

      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';

      // Prevent background scrolling, including on mobile
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';

      return () => {
          html.style.overflow = previousHtmlOverflow;
          body.style.overflow = previousBodyOverflow;
          body.style.position = previousBodyPosition;
          body.style.top = previousBodyTop;
          body.style.width = previousBodyWidth;

          window.scrollTo(0, scrollY);
      };
  }, [showUploadModal]);

  const UploadWarnings = ({ message, warnings = [] }) => {
      return (
        <div className="flex max-h-[400px] h-fit flex-col pl-2 pr-1">
            <div className={`flex items-center gap-2 ${warnings?.length > 0 ? 'text-amber-500' : 'text-green-500'}`}>
                <FontAwesomeIcon icon={warnings?.length > 0 ? faCircleExclamation : faCircleCheck}/>
                <h3 className="text-lg font-semibold">
                    {message}
                </h3>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-2">
                {warnings?.length > 0 && (
                    <div className="mt-4">
                        <strong>Warnings:</strong>
                        <ul className="list-disc space-y-1 pl-5">
                            {warnings.map((warning, index) => (
                                <li className='text-[15px]' key={index}>{warning}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="mt-5 flex shrink-0 justify-end">
                <button
                    type="button"
                    onClick={() => {
                      setShowUploadModal(false)
                      addUploadedRowsToGroups(uploadRows)
                    }}
                    className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                >
                    OK
                </button>
            </div>
        </div>
      );
  };

  const openSlideout = (type, options = {}) => {
    const {
      staff = null,
      mode: slideMode = 'create',
      isArchived = false,
      title = '',
    } = options

    if (type === 'create') {
      setSelectedStaff(staff)
      setMode(slideMode)
      setIsArchivedContext(isArchived)
    }

    setSlideOutTitle(title)
    setActiveSlideOut(type)
  }

  const isExpired = (expiry) => {
    if (!expiry) return false
    const d = moment(expiry, ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
    return d.isValid() && !d.isAfter(moment().startOf('day'))
  }

  const matchesSearch = (member, term) => {
    if (!term) return true
    const query = term.trim().toLowerCase()
    const full = `${member.forename ?? ''} ${member.surname ?? ''}`.trim().toLowerCase()
    const raw = (member.name ?? '').toLowerCase()
    return full.includes(query) || raw.includes(query)
  }

  const isForceLive = (m) => m.has_open_claim === true
  const isSoftDeleted = (m) => (m.active === 0 || m.deletedFlag === true) && !isForceLive(m)
  const isArchivedFromLive = (m) => !isForceLive(m) && (isExpired(m.expiry) || isSoftDeleted(m))

  // const fetchOffPolicyStaff = async (policyId) => {
  //   try {
  //     const orgId = JSON.parse(localStorage.getItem('orgType'))?.id
  //     if (!orgId || !policyId) return

  //     const response = await axios.get(`/v1/staff-hub/policys/staff_off_policy/${policyId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`)
  //     const staffs = response.data?.staffs ?? []
  //     const grouped = staffs.reduce((acc, member) => {
  //       const title = (member.staff_type || 'Other').trim()
  //       if (!acc[title]) acc[title] = []
  //       const [forename, ...rest] = (member.name || '').split(' ')
  //       acc[title].push({
  //         id: member.staff_id,
  //         forename: forename || '',
  //         surname: rest.join(' ') || '',
  //         dob: member.date_of_birth || '',
  //         inception: member.inception_date || '',
  //         expiry: member.end_date || '',
  //         fte: member.fte || '',
  //         staff_type: member.staff_type || '',
  //         active: member.active,
  //         policy_staff_status: member.policy_staff_status,
  //         isSubmitted: member.isSubmitted,
  //         policy_staff_id: member.policy_staff_id,
  //         is_renewal: member.is_renewal,
  //         paid: member.paid,
  //         personal_days: member.personal_days,
  //         sickness_days: member.sickness_days,
  //         staff_changes_inception_date: member.staff_changes_inception_date,
  //         changes: member.changes,
  //         created_at: member.created_at,
  //         policy_id: member.policy_id,
  //       })
  //       return acc
  //     }, {})

  //     setArchivedGroupedStaff(grouped)
  //     // createInfo('success', 'Successfully retrieved archived staff')
  //   } catch (err) {
  //     createInfo('error', 'Failed to fetch archived staff')
  //     console.error(err)
  //   }
  // }

  const fetchStaff = async ({ background = false } = {}) => {
    try {
      if (!background) setIsLoading(true)
      let orgId = JSON.parse(localStorage.getItem('orgType'))?.id;
      if (!orgId) {
        orgId = JSON.parse(localStorage.getItem('policyData'))?.policyable_id
      }

      if (!orgId) return

      const response = await axios.get(`/v1/staff-hub/policys/org_policys/academy/${orgId}?api_token=${process.env.REACT_APP_KIS_TOKEN}&area=staffs`);
      const groupList = response.data[0]?.groupList || [];

      const formattedStaff = groupList.reduce((acc, group) => {
        acc[group.title.trim()] = group.staff.map(member => ({
          id: member.staff_id,
          forename: member.name?.split(' ')[0] || '',
          surname: member.name?.split(' ').slice(1).join(' ') || '',
          dob: member.date_of_birth || '',
          inception: member.inception_date || '',
          expiry: member.end_date || '',
          fte: member.fte || '',
          staff_type: member.staff_type || '',
          policy_staff_id: member.policy_staff_id,
          policy_staff_status: member.policy_staff_status,
          has_open_claim: member.staff?.has_open_claim ?? member.staff?.has_open_claims ?? false,
          isSubmitted: member.isSubmitted,
          is_renewal: member.is_renewal,
          paid: member.paid,
          personal_days: member.personal_days,
          sickness_days: member.sickness_days,
          staff_changes_inception_date: member.staff_changes_inception_date,
          changes: member.changes,
          created_at: member.created_at,
          policy_id: member.policy_id,
        }))
        return acc
      }, {})
      // createInfo('success', 'Successfully retrieved staff list');
      setGroupedStaff(formattedStaff);
    } catch (err) {
      createInfo('error', 'Failed to fetch staff list');
    } finally {
      if (!background) setIsLoading(false)
    }
  }

  const refreshStaffLists = async ({ showLoading = false } = {}) => {
    if (showLoading) setIsLoading(true)
    try {
      await Promise.all([
        fetchStaff({ background: true }),
        // fetchOffPolicyStaff(policyData?.policy_id),
      ])
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }

  const filteredGroupedStaff = useMemo(() => {
    const query = searchTerm
    return Object.fromEntries(
      Object.entries(groupedStaff)
        .map(([group, members]) => [
          group,
          members.filter(staff => {
            const inTab = activeTab === 'live'
              ? (isForceLive(staff) || (!isExpired(staff.expiry) && !isSoftDeleted(staff)))
              : (!isForceLive(staff) && (isExpired(staff.expiry) || isSoftDeleted(staff)))
            return inTab && matchesSearch(staff, query);
          })
        ])
    )
  }, [groupedStaff, activeTab, searchTerm])

  const mergedArchivedGroupedStaff = useMemo(() => {
    const result = {}

    for (const [group, members] of Object.entries(archivedGroupedStaff)) {
      result[group] = [...members]
    }

    // add on-policy items that are archived (expired/soft-deleted) but not force-live
    for (const [group, members] of Object.entries(groupedStaff)) {
      const toAdd = members.filter(isArchivedFromLive)

      if (!result[group]) result[group] = [];
      const seen = new Set(result[group].map(m => m.id))
      toAdd.forEach(m => {
        if (!seen.has(m.id)) result[group].push(m)
      });
    }

    return result;
  }, [groupedStaff, archivedGroupedStaff])

  const filteredArchivedGroupedStaff = useMemo(() => {
    const query = searchTerm;
    return Object.fromEntries(
      Object.entries(mergedArchivedGroupedStaff)
        .map(([group, members]) => [
          group,
          members.filter(member => matchesSearch(member, query))
        ])
    )
  }, [mergedArchivedGroupedStaff, searchTerm])

  const activeGroupedStaff = activeTab === 'archived' ? filteredArchivedGroupedStaff : filteredGroupedStaff

  const orderedActiveGroupEntries = useMemo(() => {
    const getCategoryRank = (category) => {
      const normalized = String(category || '').trim().toLowerCase()
      if (normalized === 'teachers') return 0
      if (normalized === 'support') return 1
      return 2
    }

    return Object.entries(activeGroupedStaff)
      .map(([group, members], index) => ({ group, members, index }))
      .sort((a, b) => {
        const rankDiff = getCategoryRank(a.group) - getCategoryRank(b.group)
        if (rankDiff !== 0) return rankDiff
        return a.index - b.index
      })
      .map(({ group, members }) => [group, members])
  }, [activeGroupedStaff])

  const liveCount = Object.values(groupedStaff)
    .flat()
    .filter(m => isForceLive(m) || (!isExpired(m.expiry) && !isSoftDeleted(m))).length

  const expiredCount = Object.values(groupedStaff)
    .flat()
    .filter(m => !isForceLive(m) && (isExpired(m.expiry) || isSoftDeleted(m))).length

  const archivedCount = Object.values(mergedArchivedGroupedStaff).flat().length

  const hasDrafts = Object.values(groupedStaff)
    .flat()
    .some(m => m.policy_staff_status === 'draft');

  const { hasDraftsLive, hasDraftsExpired, hasDraftsArchived, hasDraftsAny } = useMemo(() => {
    const allLive = Object.values(groupedStaff).flat()
    const allArchived = Object.values(archivedGroupedStaff).flat()

    const expiredFromLive = allLive.filter(m => isExpired(m.expiry) || isSoftDeleted(m))

    const hasDraftsLive = allLive.some(m => m.policy_staff_status === 'draft')
    const hasDraftsExpired = expiredFromLive.some(m => m.policy_staff_status === 'draft')
    const hasDraftsArchived = allArchived.some(m => m.policy_staff_status === 'draft')

    return {
      hasDraftsLive,
      hasDraftsExpired,
      hasDraftsArchived,
      hasDraftsAny: hasDraftsLive || hasDraftsExpired,
    };
  }, [groupedStaff, archivedGroupedStaff])

  const hasUnsavedChanges = hasDraftsAny

  useEffect(() => {
    setPolicyData(JSON.parse(localStorage.getItem('policyData')))
    setOrgType(JSON.parse(localStorage.getItem('orgType')))
    fetchStaff()
  }, [])

  // useEffect(() => {
  //   if (policyData?.policy_id) 
  //     fetchOffPolicyStaff(policyData?.policy_id)
  // }, [policyData])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasUnsavedChanges || submitPressed.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    setGroupPages({})
  }, [activeTab, searchTerm])

  const handleStaffUpdate = (group, updatedStaff) => {
    setGroupedStaff(prev => ({
      ...prev,
      [group]: prev[group].map(member =>
        member.id === updatedStaff.id ? updatedStaff : member
      )
    }))
  }
  const handleAddStaff = () => {
    openSlideout('create', {
      mode: 'create',
      staff: null,
      isArchived: false,
      title: (
        <>
          Create a new Staff Member
          <p className="text-xs">
            Please complete the required information below to submit your request.
          </p>
        </>
      )
    })
  }

  const handleEditStaff = (staff, fromArchived = false) => {
    openSlideout('create', {
      mode: 'edit',
      staff,
      isArchived: fromArchived,
      title: (
        <>
          {`Edit details for ${staff?.forename + ' ' + staff?.surname || ''}`}
          <p className="text-xs">
            Please complete the required information below to submit your request.
          </p>
        </>
      )
    })
  }

  const handleUploadCsv = () => {
    openSlideout('upload', {
      title: (
        <>
          {`Upload your Staff List`}
          <p className="text-xs">
            Please read the instruction guide before uploading your staff list.
          </p>
        </>
      )
    })
  }

  const handleDownloadStaffList = async () => {
    const policyId = policyData?.policy_id
    if (!policyId) {
      createInfo('error', 'Missing policy information')
      return
    }

    setIsDownloadingStaffList(true)
    try {
      const response = await axios.get(`/v1/staff-hub/policy_staffs/download-staff-list/${policyId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        responseType: 'blob',
      })

      const contentDisposition = response?.headers?.['content-disposition'] || ''
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
      const downloadName = decodeURIComponent("policy_staff_list.csv")
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: response?.headers?.['content-type'] || 'text/csv' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', downloadName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading staff list:', error)
      createInfo('error', 'Failed to download staff list')
    } finally {
      setIsDownloadingStaffList(false)
    }
  }

  const handleStaffDeleted = (deletedStaffId) => {
    setGroupedStaff(prev => {
      const next = { ...prev };
      for (const [group, members] of Object.entries(next)) {
        next[group] = members.map(m =>
          m.id === deletedStaffId
            ? {
              ...m,
              expiry: moment().format('YYYY-MM-DD'),
              active: 0,
              policy_staff_status: 'draft',
              isSubmitted: 0,
              deletedFlag: true,
            }
            : m
        )
      }
      return next
    })

    setArchivedGroupedStaff(prev => {
      const next = {}
      Object.entries(prev).forEach(([group, members]) => {
        next[group] = members.filter(m => m.id !== deletedStaffId)
      })
      return next
    })
  }

  const attemptNavigate = (href) => {
    if (hasUnsavedChanges) {
      setPendingHref(href)
      setShowConfirm(true)
    } else {
      if (href) {
        navigate(href);
      }
    }
  }

  const confirmLeave = () => {
    setShowConfirm(false)
    if (pendingHref) navigate(pendingHref)
    setPendingHref(null)
  }

  const cancelLeave = () => {
    setShowConfirm(false);
    setPendingHref(null);
  }

  const revertAllStaffChanges = async () => {
    if (isRevertingAll) return
    const policyId = policyData?.policy_id;
    if (!policyId) {
      createInfo('error', 'Missing policy ID');
      return;
    }

    try {
      setShowConfirmModal(false)
      setIsRevertingAll(true)
      await axios.post(`/v1/staff-hub/policy_staffs/revert/all/${policyId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`)
      createInfo('success', 'Changes reverted successfully')

      await refreshStaffLists()
    } catch (err) {
      console.error('Revert error:', err)
      createInfo('error', 'Failed to revert changes')
    } finally {
      setIsRevertingAll(false)
    }
  }

  const removeFromArchived = (staffId, staffType) => {
    setArchivedGroupedStaff(prev => {
      const group = (staffType || '').trim()
      if (!prev[group]) return prev
      const next = { ...prev }
      next[group] = next[group].filter(m => m.id !== staffId)
      return next
    })
  }

  const addUploadedRowsToGroups = (rows) => {
    const byGroup = {};

    rows.forEach(r => {
      const groupTitle = (r.staff_type || 'Other').trim();
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const mapped = {
        id: tempId,
        forename: r.forename ?? '',
        surname: r.surname ?? '',
        dob: r.date_of_birth ?? '',
        inception: r.inception_date || moment().format('YYYY-MM-DD'),
        expiry: r.end_date || '',

        fte: r.fte ?? '',
        staff_type: groupTitle,
        policy_staff_status: 'draft',
        isSubmitted: 0,
        active: 0,
      }

      if (!byGroup[groupTitle]) byGroup[groupTitle] = [];
      byGroup[groupTitle].push(mapped)
    })

    setGroupedStaff(prev => {
      const next = { ...prev }
      Object.entries(byGroup).forEach(([group, items]) => {
        next[group] = [...(next[group] ?? []), ...items]
      })
      return next
    })

    fetchStaff()
  }

  const getTotalFte = () => {
    const totalFte = []
    let zeroFteCount = 0

    Object.entries(groupedStaff).forEach(([groupTitle, staffList]) => {
      let groupFte = 0

      staffList.forEach(staff => {
        const fte = parseFloat(staff.fte) || 0
        groupFte += fte
        if (fte === 0) {
          zeroFteCount++
        }
      })

      totalFte.push({
        title: groupTitle,
        fte: groupFte,
      })
    })

    return { totalFte, zeroFteCount }
  }

  const handleBulkExpire = async (ids) => {
    try {
      if (selectedStaffIds.length === 0) {
        createInfo('error', 'No staff selected')
        return
      }

      await axios.post(`/v1/staff-hub/policy_staffs/bulk-expire?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        policy_staff_ids: selectedStaffIds,
      })

      createInfo('success', 'Selected staff expired successfully')
      setSelectedStaffIds([])
      fetchStaff()
      setBulkMode(false)
    } catch (err) {
      createInfo('error', 'Failed to expire staff')
      console.error(err)
    }
  }

  const submitPolicyStaff = async () => {
    if (!policyData?.policyable_id || !orgType?.type) {
      createInfo('error', 'Organisation info missing');
      return;
    }

    try {
      setIsSubmitting(true)
      submitPressed.current = true
      const groupList = []
      const totalFte = []
      let zeroFteCount = 0

      Object.entries(groupedStaff).forEach(([title, members]) => {
        let fte = 0
        const staff = members.map((m) => {
          const f = parseFloat(m.fte) || 0
          fte += f
          if (f === 0) zeroFteCount++
          return m
        })

        groupList.push({
          title: title.trim(),
          fte,
          policy_id: policyData.policy_id,
          staff,
        })

        totalFte.push({
          title: title.trim(),
          fte,
        })
      })

      if (zeroFteCount > 0) {
        createInfo('warning', `${zeroFteCount} staff member(s) have 0.00 FTE`)
      }

      const payload = {
        group: groupList,
        all: [
          {
            organization_id: policyData.policyable_id,
            organization_type: orgType.type,
            organization_name: orgType.name ?? 'Unknown School',
            groupList,
            offerPopup: 0,
            policy_data: {
              policy_id: policyData.policy_id,
              schedule: policyData.schedule,
              policy_status: policyData.policy_status,
              policy_inception: policyData.policy_inception,
              policy_expire: policyData.policy_expire,
              department: policyData.department,
              product: policyData.product,
              ...policyData
            }
          }
        ],
        total_fte: totalFte,
      }

      const response = await axios.post(`/v1/staff-hub/policy_staffs/submit?api_token=${process.env.REACT_APP_KIS_TOKEN}`, payload)

      createInfo('success', response.data?.message || 'Staff submitted successfully')
      localStorage.removeItem('hasChanges')

      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      console.error('Failed submitting staff', error)
      createInfo('error', 'Something went wrong submitting staff.')
    } finally {
      setIsSubmitting(false)
      setCanSubmit(false)
    }
  }

  const isSlideOutOpen = activeSlideOut !== null

  const renderActiveSlideOut = () => {
    switch (activeSlideOut) {
      case 'create':
        return (
          <CreateStaff
            setOpen={setCreateSlideOutOpen}
            mode={mode}
            staff={selectedStaff}
            policyData={policyData}
            staffGroups={Object.keys(groupedStaff)}
            onStaffAddedOrUpdated={refreshStaffLists}
            onCreatingChange={setIsLoading}
            onStaffDeleted={handleStaffDeleted}
            isArchivedContext={isArchivedContext}
            onArchivedStaffRemoved={removeFromArchived}
          />
        )
      case 'upload':
        return (
          <UploadStaff
            setOpenCsv={setUploadSlideOutOpen}
            policyData={policyData}
            onUploaded={() => setShowUploadModal(true)}
            setUploadWarnings={setUploadWarnings}
            setUploadMessage={setUploadMessage}
            setShowModal={setShowUploadModal}
            setRows={setUploadRows}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="w-full">
      {showConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
              <FontAwesomeIcon icon={faWarning} className="size-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-center mt-4 mb-2">Unsaved changes</h3>
            <p className="mt-4 text-gray-600 text-center mb-8">
              You have unsaved changes. Navigating away will remove these changes.
            </p>
            <div className="pt-2 flex items-center justify-between">
              <button
                onClick={confirmLeave}
                className="inline-flex items-center border border-red-600 bg-red-600 hover:shadow-lg rounded-md overflow-hidden text-sm font-medium"
              >
                <span className="px-4 py-2 text-white">Leave Anyway</span>
              </button>
              <button
                onClick={cancelLeave}
                className="inline-flex items-center border border-sas bg-sas hover:shadow-lg rounded-md overflow-hidden text-sm font-medium"
              >
                <span className="px-4 py-2 text-white">Stay</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* {canSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white max-w-md w-full rounded-lg p-6 shadow-lg">
            <div className="flex justify-center mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-yellow-400 rounded-full bg-yellow-50 p-4 border border-yellow-200" />
            </div>
            <h2 className="text-center text-xl font-semibold text-gray-800">Are you sure?</h2>
            <p className="mt-2 text-center text-gray-600 text-sm">
              Are you sure you want to submit these staff details for your school?
            </p>
            <div className="mt-6 flex justify-center items-center gap-4">
              <button
                onClick={() => setCanSubmit(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={submitPolicyStaff}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Yes, submit staff'}
              </button>
            </div>
          </div>
        </div>
      )} */}
      <>
        {showConfirmModal && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
                <FontAwesomeIcon icon={faArrowRotateLeft} className="size-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 text-center mt-4 mb-2">
                Revert all unsubmitted changes?
              </h2>
              <p className="mt-4 text-sm text-gray-600 text-center mb-8">
                This will revert the changes you have made to all <strong>unsubmitted</strong> staff members. This action cannot be undone.
              </p>
              <div className="pt-2 flex items-center justify-between">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isRevertingAll}
                  className="inline-flex items-center border border-slate-300 bg-white hover:shadow-lg rounded-md overflow-hidden text-sm font-medium"
                >
                  <span className="px-4 py-2 text-slate-700">Cancel</span>
                </button>
                <button
                  onClick={revertAllStaffChanges}
                  disabled={isRevertingAll}
                  className="inline-flex items-center border border-red-600 bg-red-600 hover:shadow-lg rounded-md overflow-hidden text-sm font-medium"
                >
                  <span className="px-4 py-2 text-white">{isRevertingAll ? 'Reverting...' : 'Yes, revert'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </>
      {showUploadModal && 
        <Modal title={uploadWarnings?.length > 0 ? 'Upload Completed with Warnings' : 'Upload Completed'} body={<UploadWarnings message={uploadMessage} warnings={uploadWarnings}/>} show={() => setShowUploadModal(false)}/>
      }
      {/* Main Content Layout */}
      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6">
        {/* Main Card Section */}
        <main className="w-full space-y-4">
          <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
            {hasDraftsAny && (
              <div className="justify-self-start rounded-md border border-orange-600 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-600 shadow-md sm:col-start-1 sm:row-start-1">
                This staff list has unsaved changes. Please submit these else they will be lost
              </div>
            )}
            {/* Actions dropdown */}
            <Menu as="div" className="relative justify-self-end sm:col-start-2 sm:row-start-1">
              <Menu.Button className="inline-flex items-center gap-3 rounded-md border border-sas bg-white px-4 py-2 text-sm font-medium text-sas shadow-md hover:shadow-lg">
                Edit Staff List
                <FontAwesomeIcon icon={faChevronDown} />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-20 mt-2 w-60 rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={handleAddStaff}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-sas ${active ? 'bg-gray-100' : ''}`}
                    >
                      <FontAwesomeIcon icon={faPlus} fixedWidth />
                      Add Staff Member
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={handleUploadCsv}
                      data-tooltip-id={hasDraftsAny ? 'staff-bulk-upload-info' : undefined}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-sas ${active ? 'bg-gray-100' : ''}`}
                    >
                      <FontAwesomeIcon icon={faUpload} fixedWidth />
                      Add Staff in Bulk
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item disabled={isDownloadingStaffList}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={handleDownloadStaffList}
                      disabled={isDownloadingStaffList}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-sas ${active ? 'bg-gray-100' : ''} ${isDownloadingStaffList ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <FontAwesomeIcon icon={isDownloadingStaffList ? faSpinner : faDownload} spin={isDownloadingStaffList} fixedWidth />
                      {isDownloadingStaffList ? 'Downloading...' : 'Download Staff List'}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item disabled={isDownloadingStaffList}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => setBulkMode(true)}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 ${active ? 'bg-gray-100' : ''}`}
                    >
                      <FontAwesomeIcon icon={faCancel} fixedWidth />
                      Bulk Expire
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
              <Tooltip id="staff-bulk-upload-info" place="top">
                Please submit your changes before uploading a xlsx
              </Tooltip>
            </Menu>
            {/* Search Input (Left) */}
            <div className="relative w-full sm:w-72 sm:col-start-1 sm:row-start-2">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search staff"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Live / Expired Tabs (Right) */}
            {/* <div className="inline-flex rounded-md shadow-sm overflow-hidden border bg-white justify-self-start sm:justify-self-end sm:col-start-2 sm:row-start-2">
              <button
                onClick={() => setActiveTab('live')}
                className={`px-4 py-2 flex items-center text-sm font-medium ${activeTab === 'live' ? 'bg-sas text-white' : 'text-sas'
                  }`}
              >
                Live <span className="mx-1 opacity-80">({liveCount})</span>{' '}
                {hasDraftsLive === true && (
                  <div
                    className={`text-xs ${activeTab === 'live' ? 'text-orange-100' : 'text-orange-600'
                      }`}
                    aria-label="Has drafts"
                  >
                    (Unsaved Changes)
                  </div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`px-4 py-2 text-sm flex items-center font-medium border-l ${activeTab === 'archived' ? 'bg-sas text-white' : 'text-sas'
                  }`}
              >
                Expired <span className="mx-1 opacity-80">({archivedCount})</span>
              </button>
            </div> */}
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center py-8 text-sas">
              <div>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              </div>
              <div className='text-sas mt-4'>Loading Staff List...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'live' && (
                <div className="flex justify-end items-center my-4">
                  {bulkMode && (
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 bg-gray-300 rounded"
                        onClick={() => {
                          setBulkMode(false)
                          setSelectedStaffIds([])
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50"
                        disabled={selectedStaffIds.length === 0}
                        onClick={() => handleBulkExpire(selectedStaffIds)}
                      >
                        Expire Selected ({selectedStaffIds.length})
                      </button>
                    </div>
                  )}

                </div>
              )}

              {orderedActiveGroupEntries.map(([group, members], index) => {
                const groupKey = `${activeTab}:${group}`
                const totalPages = Math.max(1, Math.ceil(members.length / pageSize))
                const currentPage = Math.min(groupPages[groupKey] || 1, totalPages)
                const pageStartIndex = (currentPage - 1) * pageSize
                const pageMembers = members.slice(pageStartIndex, pageStartIndex + pageSize)
                const showingFrom = members.length === 0 ? 0 : pageStartIndex + 1
                const showingTo = Math.min(pageStartIndex + pageMembers.length, members.length)

                return (
                  <div key={group} className="bg-white shadow rounded p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{index+1}. {activeTab === 'archived' ? `${group} (Expired)` : group}</h4>
                    </div>

                    <table className="w-full text-sm text-left text-gray-800 border border-gray-300 rounded-lg overflow-hidden">
                      <thead className="bg-sas text-white">
                        <tr className="text-sm text-left">
                          {bulkMode && activeTab === 'live' && (
                            <th className="p-2">
                              <input
                                type="checkbox"
                                checked={pageMembers.length > 0 && pageMembers.every((m) => selectedStaffIds.includes(m.policy_staff_id))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedStaffIds((prev) => [
                                      ...new Set([...prev, ...pageMembers.map((m) => m.policy_staff_id)]),
                                    ])
                                  } else {
                                    setSelectedStaffIds((prev) =>
                                      prev.filter((id) => !pageMembers.some((m) => m.policy_staff_id === id))
                                    )
                                  }
                                }}
                              />
                              <span className="pl-2">Select</span>
                            </th>
                          )}
                          <th className="p-2 pl-4">Name</th>
                          <th className="p-2">Surname</th>
                          <th className="p-2">Date of birth</th>
                          <th className="p-2">FTE</th>
                          <th className="p-2">Staff Type</th>
                          <th className="p-2">Status</th>
                          <th className="p-2">Inception</th>
                          <th className="p-2">Expiry</th>
                          {!bulkMode && <th className="p-2">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {members.length === 0 && (
                          <tr>
                            <td
                              colSpan={8 + (!bulkMode ? 1 : 0) + (bulkMode && activeTab === 'live' ? 1 : 0)}
                              className="p-4 text-center text-gray-500"
                            >
                              {searchTerm.trim()
                                ? 'No staff match your search in this group.'
                                : `No ${activeTab === 'archived' ? 'expired' : 'live'} staff in this group.`}
                            </td>
                          </tr>
                        )}
                        {pageMembers.map((m) => (
                          <tr
                            key={m.id}
                            className={`border-t ${m.policy_staff_status === 'draft' ? 'bg-orange-300' : 'odd:bg-blue-50 even:bg-white'} ${activeTab === 'archived' || isExpired(m.expiry) || isSoftDeleted(m) ? 'text-red-700' : ''}`}
                          >
                            {bulkMode && activeTab === 'live' && (
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  checked={selectedStaffIds.includes(m.policy_staff_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedStaffIds((prev) => [...prev, m.policy_staff_id])
                                    } else {
                                      setSelectedStaffIds((prev) =>
                                        prev.filter((id) => id !== m.policy_staff_id)
                                      )
                                    }
                                  }}
                                />
                              </td>
                            )}
                            <td className="p-2 pl-4">{m.forename}</td>
                            <td className="p-2">{m.surname}</td>
                            <td className="p-2">{moment(m.dob).format('DD MMM YYYY')}</td>
                            <td className="p-2">{m.fte}</td>
                            <td className="p-2">{m.staff_type || group}</td>
                            <td className="p-2">{m.policy_staff_status || '-'}</td>
                            <td className="p-2">{moment(m.inception).format('DD MMM YYYY')}</td>
                            <td className="p-2">{moment(m.expiry).format('DD MMM YYYY')}</td>
                            {!bulkMode && (
                              <td className="p-2">
                                <button
                                  className="text-sas hover:text-sas-dark"
                                  onClick={() => handleEditStaff(m, activeTab === 'archived')}
                                >
                                  Edit
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
                      <span>Showing {showingFrom}-{showingTo} of {members.length}</span>
                      <div className="flex items-center gap-2">
                        <button
                          className="px-3 py-1 border rounded disabled:opacity-40"
                          disabled={currentPage <= 1}
                          onClick={() => setGroupPages((prev) => ({ ...prev, [groupKey]: Math.max(1, currentPage - 1) }))}
                        >
                          Prev
                        </button>
                        <span>{currentPage} / {totalPages}</span>
                        <button
                          className="px-3 py-1 border rounded disabled:opacity-40"
                          disabled={currentPage >= totalPages}
                          onClick={() => setGroupPages((prev) => ({ ...prev, [groupKey]: Math.min(totalPages, currentPage + 1) }))}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <div className="fixed bottom-0 left-0 right-0 z-50 flex gap-5 mt-2 bg-white border-t border-gray-200 p-3 shadow-lg justify-end pr-5">
            {!hasDraftsAny && (
              <Tooltip anchorSelect=".no-drafts-submit" place="top">
                No Changes To Submit
              </Tooltip>
            )}
            <span className={!hasDraftsAny ? 'no-drafts-submit' : ''}>
              <button
                onClick={() => submitPolicyStaff()}
                disabled={isLoading || !hasDraftsAny}
                className={`inline-flex items-center border shadow-md rounded-md overflow-hidden text-sm font-medium ${hasDraftsAny
                  ? 'hover:shadow-lg bg-white border-sas text-sas'
                  : 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                  }`}
              >
                <span className="px-4 py-2 text-2xl font-semibold">Submit Staff Details</span>
                <span className={`px-3 py-2 text-2xl ${hasDraftsAny ? 'bg-sas text-white' : 'bg-gray-200 text-gray-500'}`}>
                  <FontAwesomeIcon icon={faPlus} />
                </span>
              </button>
            </span>
            {(hasDraftsAny) && (
              <button
                onClick={() => setShowConfirmModal(true)}
                className={`inline-flex items-center border shadow-md hover:shadow-lg bg-white ${activeView === 'form' ? 'border-orange-600 text-orange-600' : 'border-white text-white'
                  } rounded-md overflow-hidden text-sm font-medium`}
              >
                <span className="px-4 py-2 text-2xl font-semibold">Revert Unsubmitted Changes</span>
                <span className="bg-orange-600 text-white px-3 py-2 text-2xl">
                  <FontAwesomeIcon icon={faArrowRotateLeft} />
                </span>
              </button>
            )}
          </div>
        </main>
      </div>
      <Slideout
        open={isSlideOutOpen}
        setOpen={setSlideOutOpen}
        title={slideOutTitle}
        width={activeSlideOut === 'upload' ? 'md' : 'sm'}
      >
        {renderActiveSlideOut()}
      </Slideout>
    </div>
  )
}

export default StaffDetails
